import { toDate } from 'date-fns';
import { useCallback } from 'react';
import type { Preset } from '@/hooks/date';
import { calculateDuration, useFormatDate, useFormatDuration } from '@/hooks/date';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
type DateLike = string | number | Date;
type Options = {
  validFrom: DateLike;
  validTo?: DateLike;
};
type WithFormatStrOptions = Options & {
  formatStr?: keyof Preset;
  isShort?: boolean;
};
export default function useFormatValidDuration() {
  const formatDate = useFormatDate();
  const formatDuration = useFormatDuration();
  const tc = useCommonTranslation();
  const formatDurationText = useCallback((years: number, months: number, days: number) => years === 0 && months === 0 && days === -1 ? '' : years === 0 && months === 0 ? tc('date.lessThanOneMonth') : formatDuration({
    years,
    months,
    days
  }), [formatDuration, tc]);
  const formatDateRange = useCallback((from: Date, to: Date | null, formatStr?: {
    preset: keyof Preset;
  }, isShort?: boolean) => tc(isShort ? 'date.dateRangeShort' : 'date.dateRange', {
    from: formatDate(from, formatStr),
    to: to ? formatDate(to, formatStr) : ''
  }), [formatDate, tc]);

  /**
   * 開始日と終了日を返す。toがnullの場合は開始日とチルダを返す。
   *
   * ex) ja: 2024年11月2日 〜 2024年11月13日
   *     en: Nov 2, 2024 - Nov 13, 2024
   *
   * ex) ja: 2024年11月2日 〜
   *     en: Nov 2, 2024 -
   */
  const dateRange = useCallback((options: WithFormatStrOptions) => {
    const to = toDate(options.validTo ?? new Date());
    const from = toDate(options.validFrom);
    return formatDateRange(from, options.validTo ? to : null, options.formatStr && {
      preset: options.formatStr
    }, options.isShort);
  }, [formatDateRange]);
  /**
   * 開始日から終了日までの期間を返す。toがnullの場合は今日までの期間とする。
   * 1ヶ月未満の場合は、"1ヶ月未満"と表示する。
   *
   * ex) ja: 1年1ヶ月
   *     en: 1 year 1 month
   */
  const duration = useCallback((options: Options) => {
    const to = toDate(options.validTo ?? new Date());
    const from = toDate(options.validFrom);
    const {
      years,
      months,
      days
    } = from <= new Date() ? calculateDuration(from, to) : {
      years: 0,
      months: 0,
      days: -1
    }; // 0日の時は1ヶ月未満と出したいので、未来日の場合は-1を渡しておく
    return formatDurationText(years, months, days);
  }, [formatDurationText]);

  /**
   * 開始日から終了日に加えて、期間を結合した文字列を返す。toがnullの場合は今日までの期間とする。
   * 1ヶ月未満の場合は、"1ヶ月未満"と表示する。
   *
   * ex) ja: 1年1ヶ月
   *     en: 1 year 1 month
   */
  const joined = useCallback((options: Options) => {
    const to = toDate(options.validTo ?? new Date());
    const from = toDate(options.validFrom);
    const durationText = duration({
      validFrom: from,
      validTo: to
    });
    return tc(durationText ? 'date.duration' : 'date.dateRange', {
      from: formatDate(from),
      to: options.validTo ? formatDate(to) : '',
      duration: durationText
    });
  }, [duration, formatDate, tc]);
  return {
    dateRange,
    duration,
    joined
  };
}